import type { ReactElement } from 'react'
import type { DetectionBoxSpec } from '../../lib/proofSteps.js'

export type DetectionVariant = 'scanning' | 'locked' | 'fail' | 'pass' | 'flag'

/**
 * Renders an absolute-positioned AR detection bounding box with a monospace label.
 *
 * The box renders the `.ar-detection-box {variant}` + `.ar-detection-label` structure
 * defined in `theme/installer.css`. Geometry comes from `DetectionBoxSpec` (top/left/width/height
 * as percentage strings). The `pass` variant reuses `.locked` styling via CSS.
 *
 * @param box     - Position and label data from `PROOF_STEPS[n].cameraBox` etc.
 * @param variant - Visual state: 'scanning' (dashed blue pulse), 'locked'/'pass' (solid green),
 *                  'fail' (solid red pulse). A box with `tone: 'flag'` renders its locked/pass
 *                  state in orange — documented-but-noteworthy (e.g. the vehicle carton crush).
 */
export const DetectionBox = ({
  box,
  variant,
}: {
  box: DetectionBoxSpec
  variant: DetectionVariant
}): ReactElement => (
  <div
    className={`ar-detection-box ${box.tone === 'flag' && (variant === 'locked' || variant === 'pass') ? 'flag' : variant}`}
    style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
  >
    <div className="ar-detection-label">{box.text}</div>
  </div>
)
